<template>
  <div class="sm-new-mail-wrap">
    <div class="sm-wrap sm">
      <div class="sm-new-mail">
        <breadcrumbs-block 
          v-if="!isMobile"
          :crumbs="breadcrumbs"
        />
        <h2>New mail</h2>
        <form 
          class="sm-new-mail__form"
          @submit.prevent
        >
          <div class="sm-new-mail__form-inner">
            <div class="sm-new-mail__form-left">
              <span v-if="backError" class="sm-error sm-small">{{backError}}</span>
              <p 
                v-if="nextMailNumber"
                class="sm-new-mail__form-left-tip"
              >Next mail: #{{nextMailNumber}}</p>
              <datepicker-wrap
                v-model="formData.received_at"
                :placeholder="new Date() | moment('MMMM D, YYYY')"
                :label="'Received on'"
                :outerVal="defaultDate"
              />
              <input-pmb-tags
                :ref="'tags'"
                :label="'Mailbox Number'"
                :error="errors.first('account_numbers')"
                :showHint="true"
                :defaultTag="defaultPMB"
                :maxTags="formData.picture ? 1 : null"
                :accountNum="scannedAccountNumber"
                @updateList="updatePMBList($event)"
              />

              <template v-if="noForm1583PMBs.length">
                <div class="sm-new-mail__form-left-no-form sm-small">
                  <template v-if="noForm1583PMBs.length == 1">
                    <p>
                      <include-svg :name="'icon-warning'" />
                      <span class="sm-error sm-txt-strong">{{noForm1583PMBs[0].name}} (Mailbox #{{noForm1583PMBs[0].pmb}}) has not signed USPS Form 1583.</span>
                      <a
                        href="#"
                        @click.prevent="openCustomer(noForm1583PMBs[0].customer.id)"
                      >Upload</a>
                    </p>
                  </template>
                  <template v-else>
                    <p>
                      <include-svg :name="'icon-warning'" />
                      <span class="sm-error sm-txt-strong">Following Users have not signed USPS Form 1583:</span>
                    </p>
                    <ul>
                      <li
                        v-for="pmb in noForm1583PMBs"
                        :key="pmb.pmb"
                      >
                        <span class="sm-error sm-txt-strong">{{pmb.name}} (Mailbox #{{pmb.pmb}})</span>
                        <a
                          href="#"
                          @click.prevent="openCustomer(pmb.customer.id)"
                        >Upload</a>
                      </li>
                    </ul>
                  </template>
                </div>
              </template>

              <template v-if="noCAForm17853PMBs.length">
                <div class="sm-new-mail__form-left-no-form sm-small">
                  <template v-if="noCAForm17853PMBs.length == 1">
                    <p>
                      <include-svg :name="'icon-warning'" />
                      <span class="sm-error sm-txt-strong">{{noCAForm17853PMBs[0].name}} (Mailbox #{{noCAForm17853PMBs[0].pmb}}) has not signed CA Document 17538.5.</span>
                      <a
                        href="#"
                        @click.prevent="openCustomer(noCAForm17853PMBs[0].customer.id)"
                      >Upload</a>
                    </p>
                  </template>
                  <template v-else>
                    <p>
                      <include-svg :name="'icon-warning'" />
                      <span class="sm-error sm-txt-strong">Following Users have not signed CA Document 17538.5:</span>
                    </p>
                    <ul>
                      <li
                        v-for="pmb in noCAForm17853PMBs"
                        :key="pmb.pmb"
                      >
                        <span class="sm-error sm-txt-strong">{{pmb.name}} (Mailbox #{{pmb.pmb}})</span>
                        <a
                          href="#"
                          @click.prevent="openCustomer(pmb.customer.id)"
                        >Upload</a>
                      </li>
                    </ul>
                  </template>
                </div>
              </template>

              <input-text
                v-model="formData.sender"
                :label="'Sender'"
                :name="'sender'"
                v-validate="'required'"
                data-vv-as="Sender"
                :error="errors.first('sender')"
              />
              <div class="sm-new-mail__form-left-flex bottom">
                <dropdown-wrap
                  v-model="selectedType"
                  :val="defaultType"
                  :options="mailTypes"
                  :capitalize="true"
                  :placeholder="'Select mail type...'"
                  :label="'Mail Type'"
                  :class="'mail-type'"
                  @input="formData.mail_type_id = selectedType.id"
                />
                <checkbox-block
                  v-model="formData.is_scannable"
                  :text="'Scannable'"
                />
              </div>
              <div class="sm-new-mail__form-left-flex">
                <input-number
                  v-model="formData.quantity"
                  :defaultVal="formData.quantity"
                  :min="1"
                  :label="'Quantity'"
                />
                <div class="sm-new-mail__form-left-flex-weight">
                  <dropdown-wrap
                    v-model="selectedWeightUnit"
                    :val="formData.weight_unit ? weightUnits.find(unit => unit.code == formData.weight_unit) : weightUnits[0]"
                    :options="weightUnits"
                    :capitalize="true"
                    :label="'Weight'"
                    @input="formData.weight_unit = selectedWeightUnit.code"
                  />
                  <input-number
                    v-model="formData.weight"
                    :defaultVal="defaultWeight"
                    :min="0"
                    :showControls="false"
                  />
                </div>
              </div>
              <input-text
                v-model="formData.tracking"
                :label="'Tracking number'"
                :name="'tracking'"
              />
              <dropdown-wrap
                v-model="selectedMessage"
                :val="defaultMessage"
                :options="messagesList"
                :placeholder="'Add message'"
                :label="'Message'"
                @input="selectMessage"
              />
              <template v-if="selectedMessage.value">
                <div
                  v-if="selectedMessage.value != 'custom'"
                  class="sm-new-mail__form-left-message"
                >{{selectedMessage.text}}</div>
                <textarea
                  v-else
                  v-model="formData.comments"
                  class="sm-new-mail__form-left-textarea"
                  placeholder="Message..."
                ></textarea>
              </template>
            </div>
            <div class="sm-new-mail__form-right">
              <upload-scan
                :name="'shipping_label'"
                :types="['image']"
                :typesText="'JPG, JPEG, PNG, HEIC, HEIF or GIF'"
                @upload="uploadFile"
                @delete="deleteFile"
              />
              <p
                class="sm-new-mail__form-right-hint sm-small"
                @mouseenter="enterHint"
                @mouseleave="leaveHint"
              >
                <include-svg :name="'icon-hint'" />
                <span>Recommendations for shipping label image</span>

                <hint-block
                  v-if="hintVisible"
                  :class="'center'"
                >
                  <template slot="hintContent">
                    <p class="sm-small">After you upload the shipping label, SphereMail scan technology will analyze the picture. When it is done, it will populate all fields it could reliably find (such as UPS/USPS/Fedex tracking number). You will also see a snapshot of the picture taken so you can confirm the information is correct before creating a new mail item.</p>
                    <span class="sm-txt-strong sm-small">Recommendations to take the picture of the shipping label:</span>
                    <ul class="sm-small">
                      <li>Ensure the test on the picture is oriented horizontally.</li>
                      <li>Ensure the shipping label is aligned and text is focused and the STE.# is visible.</li>
                      <li>Activate the flash or take the picture in an illuminated environment.</li>
                    </ul>
                  </template>
                </hint-block>
              </p>
            </div>
          </div>
        </form>
      </div>

      <action-notification :add="notif" />

    </div>

    <div class="sm-new-mail__controls">
      <div class="sm-wrap sm">
        <div class="sm-new-mail__controls-inner">
          <div class="sm-flex sm-gap-4 sm-flex-wrap max-tiny:sm-w-full">
            <btn-block
              :classes="['max-tiny:sm-w-full','sm-btn--small']"
              :text="'Save mail and add another'"
              @click="createMail()"
            />
            <btn-block
              :classes="['max-tiny:sm-w-full','btn','btn-outlined','sm-btn--small']"
              :text="'Save mail and close'"
              @click="createMail(true)"
            />
          </div>
          <btn-block
            :classes="['max-tiny:sm-w-full','sm-btn--small', 'sm-btn--grey']"
            :text="'Cancel'"
            @click="$router.push('mail')"
          />
        </div>
      </div>
    </div>

    <popup-block
      v-if="popup.isOpen"
      :open="popup.isOpen"
      :size="'medium'"
      :class="'sm-new-mail__popup'"
      @close="popup.isOpen = false;"
    >
      <template slot="popupContent">
        <div v-if="popup.type == 'upload-message'" class="sm-new-mail__popup-inner">
          <h4>Unable to recognize shipping label</h4>
          <p>{{uploadMessage}}</p>
        </div>
      </template>
    </popup-block>

    <sm-spinner :show="showSpinner" />
  </div>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs';
import DatepickerWrap from '@/components/DatepickerWrap';
import DropdownWrap from '@/components/DropdownWrap';
import InputText from '@/components/InputText';
import InputNumber from '@/components/InputNumber';
import InputPMBTags from '@/components/mail/InputPMBTags';
import Spinner from '@/components/Spinner';
import Btn from '@/components/Buttons';
import UploadScan from '@/components/mail/UploadScan';
import Checkbox from '@/components/Checkboxes';
import Popup from '@/components/Popup';
import { mapGetters, mapActions } from 'vuex';
import WeightUnits from '@/settings/weight-units';
import MessagesList from '@/settings/messages-list';
import Hint from '@/components/Hint';
import Mail from '@/settings/mail';
import ActionNotification from "@/components/ActionNotification";

export default {
  name: 'new-mail',
  components: {
    'breadcrumbs-block': Breadcrumbs,
    'datepicker-wrap': DatepickerWrap,
    'input-text': InputText,
    'input-number': InputNumber,
    'input-pmb-tags': InputPMBTags,
    'dropdown-wrap': DropdownWrap,
    'sm-spinner': Spinner,
    'btn-block': Btn,
    'upload-scan': UploadScan,
    'checkbox-block': Checkbox,
    'popup-block': Popup,
    'hint-block': Hint,
    "action-notification": ActionNotification,
  },
  data() {
    return {
      showSpinner: false,
      breadcrumbs: [
        {
          link: 'mail?tab=single',
          text: 'Mail Single'
        },
        {
          link: '/new-mail',
          text: 'New mail',
          current: true
        }
      ],
      formData: {
        received_at: '',
        account_numbers: [],
        sender: '',
        quantity: 1,
        mail_type_id: '',
        is_scannable: true,
        weight_unit: 'oz',
        weight: '',
        tracking: '',
        comments: '',
        picture: null,
      },
      selectedMessage: {},
      selectedWeightUnit: null,
      selectedType: {},
      uploadFileStatus: 'new', // values: new, upload, error
      popup: {
        isOpen: false,
        type: 'upload-message' // values: upload-message
      },
      uploadMessage: '',
      scannedAccountNumber: null,
      defaultWeight: 0,
      defaultMessage: {},
      defaultType: {},
      defaultDate: '',
      defaultPMB: {},
      weightUnits: [],
      messagesList: [],
      defaultScannableTypes: Mail.defaultScannableTypes,
      nextMailNumber: null,
      backError: '',
      hintVisible: false,
      hintTimer: null,
      selectedPMBsArr: [],
      notif: null,
      notifCount: 0,
      mail_list: [],
    };
  },
  created() {
    this.showSpinner = true;
    this.weightUnits = WeightUnits;
    this.messagesList = MessagesList;

    Promise.all([
      this.getMailTypes(),
      this.getAdminList({})
    ])
      .then(([mail_types, mail_list]) => {
        this.mail_list = mail_list

        this.defaultType = this.mailTypes.find(type => type.name.toLowerCase() == 'letter') || this.mailTypes[0];
        this.formData.mail_type_id = this.mailTypes[0].id;

        if (mail_list.mail_items && mail_list.mail_items.length)
          this.nextMailNumber = mail_list.mail_items[0].mail_number + 1;

        let query = Object.assign({}, this.$route.query);

        if (query.notarized) {
          let savedData = JSON.parse(localStorage.getItem('saved-new-mail'));
          delete query.notarized;
          this.$router.push({query});

          if (savedData) {
            localStorage.removeItem('saved-new-mail');
            if (savedData.pmb) {
              this.defaultPMB = savedData.pmb;
              this.updatePMBList([savedData.pmb.pmb]);
            }

            this.fillFormData(savedData.form);
          }
        }

        if (this.$refs.tags && this.$refs.tags.$el)
          this.$refs.tags.$el.querySelector('input').focus();
          
        this.showSpinner = false;
      });
  },
  computed: {
    ...mapGetters({
      isMobile: 'isMobile',
      mailTypes: 'mailTypes',
      provider: 'provider'
    }),
    noForm1583PMBs() {
      if (!this.provider || this.provider.client_level == 1)
        return [];

      return this.selectedPMBsArr.filter(pmb => {
        return pmb.customer && !pmb.customer.is_form_1583_signed && this.formData.account_numbers.includes(pmb.pmb);
      });
    },
    noCAForm17853PMBs() {
      if (!this.provider || this.provider.state_id != 5 || this.provider.client_level == 1)
        return [];

      return this.selectedPMBsArr.filter(pmb => {
        return pmb.customer && !pmb.customer.is_ca_form_17853_signed && this.formData.account_numbers.includes(pmb.pmb);
      });
    }
  },
  inject: ['resetRoute'],
  methods: {
    ...mapActions({
      getMailTypes: 'getMailTypes',
      createNewMail: 'mail/createNewMail',
      getShippingInfo: 'shipping/getShippingInfo',
      getAdminList: 'mail/getAdminList',
      findPMBs: 'mail/findPMBs',
      getCustomers: 'customers/getCustomers',
      getCustomer: 'customers/getCustomer'
    }),
    resetForm() {

      // init states
      // this.formData = {
      //   received_at: '',
      //   account_numbers: [],
      //   sender: '',
      //   quantity: 1,
      //   mail_type_id: '',
      //   is_scannable: true,
      //   weight_unit: 'oz',
      //   weight: '',
      //   tracking: '',
      //   comments: '',
      //   picture: null,
      // }

      this.formData.picture = null
      this.deleteFile()

      // this.selectedMessage = {}
      // this.selectedWeightUnit = null
      // this.selectedType= {}
      // this.uploadFileStatus= 'new' 
      // this.uploadMessage= ''

      // this.defaultWeight= 0
      // this.defaultMessage= {}
      // this.defaultType= {}
      // this.defaultDate= ''
      // this.defaultPMB= {}
      // this.messagesList= []
      // this.nextMailNumber= null
      // this.backError= ''
      // this.selectedPMBsArr= []



      // Reset validation (for vee-validate, call the reset method)
      // this.$refs.observer.reset(); // Assuming you have a form observer





      // created reset

      // this.defaultType = this.mailTypes.find(type => type.name.toLowerCase() == 'letter') || this.mailTypes[0];
      //   this.formData.mail_type_id = this.mailTypes[0].id;

      //   if (this.mail_list.mail_items && this.mail_list.mail_items.length)
      //     this.nextMailNumber = this.mail_list.mail_items[0].mail_number + 1;

      //   let query = Object.assign({}, this.$route.query);

      //   if (query.notarized) {
      //     let savedData = JSON.parse(localStorage.getItem('saved-new-mail'));
      //     delete query.notarized;
      //     this.$router.push({query});

      //     if (savedData) {
      //       localStorage.removeItem('saved-new-mail');
      //       if (savedData.pmb) {
      //         console.log(savedData.pmb , "tesyt")
      //         this.defaultPMB = savedData.pmb;
      //         this.updatePMBList([savedData.pmb.pmb]);
      //       }

      //       this.fillFormData(savedData.form);
      //     }
      //   }

      //   if (this.$refs.tags && this.$refs.tags.$el)
      //     this.$refs.tags.$el.querySelector('input').focus();
    },
    selectMessage(e) {
      this.formData.comments = this.selectedMessage.text;
    },
    updatePMBList(list) {
      this.formData.account_numbers = list;

      if (this.formData.account_numbers.length) {
        this.errors.remove('account_numbers');

        if (this.provider && this.provider.country_id == 1)
          this.addToSelectedPMBs(list);
      }
    },
    addToSelectedPMBs(pmbs) {
      let newPMBs = pmbs.filter(pmb => !this.selectedPMBsArr.find(item => item.pmb == pmb));

      if (!newPMBs.length)
        return;

      // this.showSpinner = true;
      this.findPMBs(newPMBs[0])
        .then(res => {
          let pmb = res.find(el => el.pmb == newPMBs[0]);

          if (!pmb) {
            // this.showSpinner = false;
            return;
          }
          
          this.getCustomers({
            query: `?search=${pmb.name.split(' ')[0]}&limit=200`
          })
            .then(customers => {
              let customer = customers.customers.find(customer => customer.pmb == pmb.pmb);
              
              if (customer) {
                this.getCustomer(customer.id)
                  .then(res => {
                    if (res && pmb.pmb == newPMBs[0]) {
                      pmb.customer = res;
                      this.selectedPMBsArr.push(pmb);
                    }

                    // this.showSpinner = false;
                  })
              } else {
                // this.showSpinner = false;
              }
            });
        });
    },
    uploadFile(file) {
      this.formData.picture = file; // to upload it on mail creation as envelope picture

      this.showSpinner = true;
      let formData = new FormData();
      formData.append('file', file);

      this.getShippingInfo(formData)
        .then(res => {
          if (res.error) {
            this.uploadMessage = res.error;
            // this.popup.type = 'upload-message';
            // this.popup.isOpen = true;
          } else {
            this.fillFormData(res);
          }
          this.showSpinner = false;
        })
        .catch(e => {
          let err = e.response;

          if (err && err.status && err.status == 422 && err.data && err.data.errors) {
            if (err.data.errors.base) {
              this.uploadMessage = err.data.errors.base[0];
            } else {
              this.uploadMessage = err.data.errors[0];
            }

            // this.popup.type = 'upload-message';
            // this.popup.isOpen = true;
          }

          this.showSpinner = false;
        });
    },
    deleteFile() {
      this.formData.picture = null;
    },
    createMail(close = false) {
      if (!this.formData.received_at)
        this.formData.received_at = this.$moment(new Date).format('YYYY-MM-DD');

      this.$validator.validate()
        .then(valid => {
          if (!this.formData.account_numbers.length) {
            this.errors.add({
              field: 'account_numbers',
              msg: 'Select at least one Mailbox#'
            });
            valid = false;
          }

          if (!valid)
            return;

          this.showSpinner = true;

          let formData = new FormData();
          Object.keys(this.formData).forEach(key => {
            if (key === 'account_numbers') {
              formData.append(`mail_item[${key}][]`, this.formData[key]);
            } else {
              formData.append(`mail_item[${key}]`, this.formData[key]);
            }
          });
          
          this.createNewMail(formData)
            .then(res => {
              if(close) {
                this.$router.push('mail');
              } else {
                window.location.reload();
              }
              // this.$router.push('mail?tab=single');
              // this.showSpinner = false;
            })
            .catch(e => {
              if (e.response) {
                let err = e.response;

                if (err.status == 404) {
                  this.backError = `Customer with Mailbox#${this.formData.account_numbers[0]} not found`;
                } else if (err.status == 422 && err.data && err.data.errors && err.data.errors.base) {
                  this.backError = err.data.errors.base[0];
                }
              }

              this.showSpinner = false;
            })
        })
    },
    enterHint() {
      this.hintTimer = setTimeout(() => this.hintVisible = true, 10);
    },
    leaveHint() {
      this.hintVisible = false;
      clearTimeout(this.hintTimer);
    },
    openCustomer(id) {
      localStorage.setItem('saved-new-mail', JSON.stringify({
        form: this.formData,
        pmb: this.selectedPMBsArr[0]
      }));
      this.$router.push(`customer/${id}?from=new`);
    },
    fillFormData(data) {
      Object.keys(data).forEach(key => {
        switch(key) {
          case 'account':
            this.formData.account_numbers = [data[key].account_number];
            this.defaultPMB = {pmb:data[key].account_number}
            break;
          case 'mail_type_id':
            this.defaultType = this.mailTypes.find(type => type.id == data[key]);
            break;
          case 'weight':
            this.defaultWeight = parseInt(data[key]);
            break;
          case 'message':
          case 'comments':
            if (data[key]) {
              this.defaultMessage = this.messagesList.find(item => item.text == data[key]);

              if (!this.defaultMessage || !this.defaultMessage.value) {
                this.defaultMessage = this.messagesList.find(item => item.value == 'custom');
                this.defaultMessage.text = data[key];
              }

              this.selectedMessage = this.defaultMessage;
              this.formData[key] = data[key];
            }
            break;
          case 'received_at':
            if (data[key]){
              this.defaultDate = data[key];
            }
            // break;
          default:
            if (this.formData.hasOwnProperty(key))
              this.formData[key] = data[key];
            break;
        }
      })
      this.formData.account_numbers = [data.account.account_number];
      this.scannedAccountNumber = data.account.account_number;
    }
  },
  watch: {
    'formData.mail_type_id'(val) {
      if (this.selectedType.name)
        this.formData.is_scannable = this.defaultScannableTypes.includes(this.selectedType.name.toLowerCase());
    },
    formData: {
      deep: true,
      handler() {
        this.backError = '';
      }
    }
  }
}
</script>

<style lang="scss">
  @use '@/assets/scss/general/mixins.scss' as *;
  @use '@/assets/scss/framework/scss-variables.scss' as *;
  @use '@/assets/scss/pages/common/_new-mail.scss';
</style>